import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Card, CardContent, CardHeader, Typography, CircularProgress, Alert, Button, TextField, MenuItem, Divider, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Snackbar, Stack, InputAdornment,
} from '@mui/material';
import {
  ArrowBack as BackIcon, Refresh as RefreshIcon, Save as SaveIcon, Key as KeyIcon, ContentCopy as CopyIcon,
  Warning as WarningIcon, CheckCircle as ConnectedIcon, Cancel as DisconnectedIcon, CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { AdminPageHeader, PlanChip, StatusChip, PLANS, STATUSES, formatLimit, usageText, errorMessage } from '../../components/Admin/adminUi';

const LIMIT_KEYS = [
  { key: 'maxUsers', label: 'Max users' },
  { key: 'maxDetections', label: 'Max detections' },
  { key: 'maxCases', label: 'Max cases' },
  { key: 'storageGB', label: 'Storage (GB)' },
];

const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

const buildForm = (company) => ({
  plan: company?.subscription?.plan || 'trial',
  status: company?.subscription?.status || 'trialing',
  trialEndsAt: toDateInput(company?.subscription?.trialEndsAt),
  limits: LIMIT_KEYS.reduce((acc, { key }) => ({ ...acc, [key]: company?.limits?.[key] ?? '' }), {}),
  notes: company?.adminNotes || '',
});

const Stat = ({ label, value }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Box>
);

const AdminCustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(buildForm(null));
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [resetLink, setResetLink] = useState(null); // { user, link }
  const [resetBusy, setResetBusy] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [companyRes, plansRes] = await Promise.all([
        apiService.admin.getCompany(id),
        apiService.admin.getPlans().catch(() => ({ data: { plans: [] } })),
      ]);
      setData(companyRes.data);
      setForm(buildForm(companyRes.data.company));
      setPlans(plansRes.data.plans || []);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load company'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const company = data?.company;
  const original = useMemo(() => buildForm(company), [company]);

  /** Only the fields that actually changed are sent; the server applies partial updates. */
  const changes = useMemo(() => {
    const out = {};
    if (form.plan !== original.plan) out.plan = form.plan;
    if (form.status !== original.status) out.status = form.status;
    if (form.trialEndsAt && form.trialEndsAt !== original.trialEndsAt) out.trialEndsAt = new Date(form.trialEndsAt).toISOString();
    const limits = {};
    LIMIT_KEYS.forEach(({ key }) => {
      if (form.limits[key] !== '' && String(form.limits[key]) !== String(original.limits[key])) limits[key] = Number(form.limits[key]);
    });
    if (Object.keys(limits).length) out.limits = limits;
    if (form.notes !== original.notes) out.notes = form.notes;
    return out;
  }, [form, original]);

  const dirty = Object.keys(changes).length > 0;

  const applyPlanDefaults = (planName) => {
    const plan = plans.find((p) => p.name === planName || p._id === planName);
    setForm((f) => ({
      ...f,
      plan: planName,
      limits: plan?.limits ? LIMIT_KEYS.reduce((acc, { key }) => ({ ...acc, [key]: plan.limits[key] ?? f.limits[key] }), {}) : f.limits,
    }));
  };

  const save = async () => {
    if (!dirty) return;
    try {
      setSaving(true);
      setError(null);
      const res = await apiService.admin.updateCompany(id, changes);
      setData((prev) => ({ ...prev, company: { ...prev.company, ...res.data.company, adminNotes: changes.notes ?? prev.company.adminNotes } }));
      setToast('Company updated');
    } catch (err) {
      setError(errorMessage(err, 'Failed to update company'));
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (isActive) => {
    setConfirmDeactivate(false);
    try {
      setSaving(true);
      setError(null);
      const res = await apiService.admin.updateCompany(id, { isActive });
      setData((prev) => ({ ...prev, company: { ...prev.company, ...res.data.company } }));
      setToast(isActive ? 'Company activated' : 'Company deactivated');
    } catch (err) {
      setError(errorMessage(err, 'Failed to update company'));
    } finally {
      setSaving(false);
    }
  };

  const generateResetLink = async (user) => {
    try {
      setResetBusy(user.id);
      setError(null);
      const res = await apiService.admin.passwordResetLink(id, user.id);
      setCopied(false);
      setResetLink({ user, link: res.data.link });
    } catch (err) {
      setError(errorMessage(err, 'Failed to generate password reset link'));
    } finally {
      setResetBusy(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink.link);
      setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };

  const handleExportTenant = async () => {
    try {
      setExporting(true);
      setExportMessage(null);
      await apiService.admin.exportCompany(id);
      setExportMessage({ severity: 'success', text: 'Tenant data export downloaded.' });
    } catch (err) {
      setExportMessage({ severity: 'error', text: errorMessage(err, 'Failed to export tenant data') });
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  if (!company) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/customers')} sx={{ mb: 2 }}>Back to customers</Button>
        <Alert severity="error">{error || 'Company not found'}</Alert>
      </Box>
    );
  }

  const users = data.users || [];
  const agents = data.agents || [];
  const counts = data.counts || {};
  const usage = company.usage || {};
  const limits = company.limits || {};

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/customers')} sx={{ mb: 1 }}>Back to customers</Button>
      <AdminPageHeader
        title={company.name}
        subtitle={[company.domain, company.industry, company.size, company.country].filter(Boolean).join(' · ') || company.slug}
        actions={(
          <>
            {!company.isActive && <Chip label="DEACTIVATED" color="error" />}
            <PlanChip plan={company.subscription?.plan} size="medium" />
            <StatusChip status={company.subscription?.status} size="medium" />
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
              disabled={exporting}
              onClick={handleExportTenant}
            >
              Export tenant data (JSON)
            </Button>
            <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          </>
        )}
      />
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {exportMessage && (
        <Alert severity={exportMessage.severity} sx={{ mb: 3 }} onClose={() => setExportMessage(null)}>
          {exportMessage.text}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Counts */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}><Stat label="Active users" value={usageText(company.userCount, limits.maxUsers)} /></Grid>
                <Grid item xs={6} sm={3}><Stat label="Detections" value={usageText(counts.detections ?? usage.currentDetections, limits.maxDetections)} /></Grid>
                <Grid item xs={6} sm={3}><Stat label="Cases" value={usageText(counts.cases ?? usage.currentCases, limits.maxCases)} /></Grid>
                <Grid item xs={6} sm={3}><Stat label="Pending invites" value={company.pendingInvites || 0} /></Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Typography variant="body2" color="text.secondary">Created: <strong>{formatDateTime(company.createdAt)}</strong></Typography>
                <Typography variant="body2" color="text.secondary">Updated: <strong>{formatDateTime(company.updatedAt)}</strong></Typography>
                <Typography variant="body2" color="text.secondary">Tenant ID: <code>{company.id}</code></Typography>
                {company.adminUser && (
                  <Typography variant="body2" color="text.secondary">
                    Admin: <strong>{company.adminUser.fullName}</strong> ({company.adminUser.email})
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Subscription" titleTypographyProps={{ variant: 'h6' }}
              action={(
                <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  disabled={!dirty || saving} onClick={save} sx={{ mt: 0.5, mr: 0.5 }}>
                  Save
                </Button>
              )}
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small" label="Plan" value={form.plan} onChange={(e) => applyPlanDefaults(e.target.value)}>
                    {(plans.length ? plans.map((p) => p.name) : PLANS).map((name) => {
                      const p = plans.find((x) => x.name === name);
                      return <MenuItem key={name} value={name}>{p?.displayName || name}{p?.price?.monthly != null ? ` — $${p.price.monthly}/mo` : ''}</MenuItem>;
                    })}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small" label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" type="date" label="Trial ends" InputLabelProps={{ shrink: true }}
                    value={form.trialEndsAt} onChange={(e) => setForm({ ...form, trialEndsAt: e.target.value })}
                    helperText={company.subscription?.trialEndsAt ? `Currently ${formatDate(company.subscription.trialEndsAt)}` : 'No trial end date set'} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Limits <Typography component="span" variant="caption" color="text.secondary">(-1 = unlimited)</Typography></Typography>
                  <Grid container spacing={1.5}>
                    {LIMIT_KEYS.map(({ key, label }) => (
                      <Grid item xs={6} key={key}>
                        <TextField fullWidth size="small" type="number" label={label} value={form.limits[key]}
                          inputProps={{ min: -1, step: 1 }}
                          onChange={(e) => setForm({ ...form, limits: { ...form.limits, [key]: e.target.value } })}
                          InputProps={{ endAdornment: <InputAdornment position="end"><Typography variant="caption" color="text.secondary">{formatLimit(form.limits[key])}</Typography></InputAdornment> }} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={3} size="small" label="Admin notes" placeholder="Internal notes about this customer (not visible to the customer)"
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} inputProps={{ maxLength: 2000 }} />
                </Grid>
              </Grid>
              {data.subscription && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Subscription record: {data.subscription.plan} / {data.subscription.status}
                  {data.subscription.stripeSubscriptionId ? ` · Stripe ${data.subscription.stripeSubscriptionId}` : ''}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Users */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title={`Users (${users.length})`} titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Last login</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary" sx={{ py: 2 }}>No users</Typography></TableCell></TableRow>
                  )}
                  {users.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Typography variant="body2">{u.fullName}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.email}{u.department ? ` · ${u.department}` : ''}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={u.role || 'n/a'} variant="outlined" /></TableCell>
                      <TableCell>
                        {u.pending ? <Chip size="small" label="Pending invite" color="warning" />
                          : u.isActive ? <Chip size="small" label="Active" color="success" />
                            : <Chip size="small" label="Inactive" />}
                      </TableCell>
                      <TableCell><Typography variant="body2" noWrap>{u.lastLogin ? formatDateTime(u.lastLogin) : 'Never'}</Typography></TableCell>
                      <TableCell align="right">
                        <Tooltip title="Generate password reset link">
                          <span>
                            <IconButton size="small" disabled={resetBusy === u.id} onClick={() => generateResetLink(u)}>
                              {resetBusy === u.id ? <CircularProgress size={18} /> : <KeyIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Agents */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title={`Agents (${agents.length})`} titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Enabled</TableCell>
                    <TableCell>Connected</TableCell>
                    <TableCell>Last successful ingestion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agents.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary" sx={{ py: 2 }}>No agents configured</Typography></TableCell></TableRow>
                  )}
                  {agents.map((a) => (
                    <TableRow key={a.id} hover>
                      <TableCell>{a.name}</TableCell>
                      <TableCell><Chip size="small" label={a.type || 'n/a'} variant="outlined" /></TableCell>
                      <TableCell><Chip size="small" label={a.isEnabled ? 'Enabled' : 'Disabled'} color={a.isEnabled ? 'success' : 'default'} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {a.isConnected ? <ConnectedIcon fontSize="small" color="success" /> : <DisconnectedIcon fontSize="small" color="disabled" />}
                          <Typography variant="body2">{a.isConnected ? 'Connected' : 'Not connected'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{a.lastSuccessfulIngestion ? formatDateTime(a.lastSuccessfulIngestion) : 'Never'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Danger zone */}
        <Grid item xs={12}>
          <Card sx={{ border: 1, borderColor: 'error.main' }}>
            <CardHeader avatar={<WarningIcon color="error" />} title="Danger zone" titleTypographyProps={{ variant: 'h6', color: 'error' }} />
            <Divider />
            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2">{company.isActive ? 'Deactivate this company' : 'Reactivate this company'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {company.isActive
                    ? 'All users of this tenant lose access immediately. Data is retained and the company can be reactivated later.'
                    : 'This company is currently deactivated. Reactivating restores access for all of its users.'}
                </Typography>
              </Box>
              {company.isActive
                ? <Button variant="outlined" color="error" disabled={saving} onClick={() => setConfirmDeactivate(true)}>Deactivate company</Button>
                : <Button variant="contained" color="success" disabled={saving} onClick={() => setActive(true)}>Reactivate company</Button>}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={confirmDeactivate} onClose={() => setConfirmDeactivate(false)}>
        <DialogTitle>Deactivate {company.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Users of this company will no longer be able to sign in or use the API until the company is reactivated. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeactivate(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => setActive(false)}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resetLink)} onClose={() => setResetLink(null)} fullWidth maxWidth="sm">
        <DialogTitle>Password reset link</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Share this link with <strong>{resetLink?.user?.fullName}</strong> ({resetLink?.user?.email}). It is single-use and expires.
          </DialogContentText>
          <TextField fullWidth size="small" value={resetLink?.link || ''} InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied ? 'Copied' : 'Copy'}><IconButton onClick={copyLink} edge="end"><CopyIcon /></IconButton></Tooltip>
              </InputAdornment>
            ),
          }} onFocus={(e) => e.target.select()} />
          {copied && <Typography variant="caption" color="success.main">Copied to clipboard</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetLink(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
};

export default AdminCustomerDetail;
